/**
 * «Требует внимания» в карточке проекта (ПФ-02, ПФ-03): находки всех разделов с кнопкой действия, фильтр по разделу,
 * сводки ПСД и ресурсов. Данные — GET /projects/:id/attention: строки собирают сами разделы, права и маски денег
 * проверяет сервер.
 */
import type { ProjectAttentionView, PsdDigest, ResourcesDigest } from '@epc/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Badge, ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

/** Ключ начинается с 'attention': любое действие, которое сбрасывает находки, обновляет и этот блок */
export const projectAttentionQuery = (projectId: string) =>
  queryOptions({
    queryKey: ['attention', 'project', projectId],
    queryFn: () => api<ProjectAttentionView>(`/projects/${projectId}/attention`),
    // Дела закрывают в других разделах (они сбрасывают только «Ждёт меня»): при каждом открытии — свежие строки
    staleTime: 0,
    refetchOnMount: 'always',
  });

const LIMIT = 12;
/** Число по-русски: 0,9 и 1 250 */
const num = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 1 });

export function ProjectAttentionPanel({ projectId }: { projectId: string }) {
  const q = useQuery(projectAttentionQuery(projectId));
  const [picked, setSource] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  // Раздел, у которого строк больше нет, — фильтр снимается сам, иначе список выглядел бы пустым без пояснения
  const source = picked && q.data?.sections.some((s) => s.source === picked) ? picked : null;
  const items = q.data ? q.data.items.filter((i) => !source || i.source === source) : [];
  const shown = all ? items : items.slice(0, LIMIT);
  return (
    <Panel title="Требует внимания" meta={q.data ? q.data.items.length : undefined}>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.items.length ? (
        <p className="text-[14px] text-ink-2">Всё в порядке: по проекту нет дел и находок.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {q.data.sections.length > 1 ? (
            <fieldset className="flex flex-wrap gap-2" aria-label="Раздел">
              <FilterChip active={!source} onClick={() => setSource(null)}>
                Все · {q.data.items.length}
              </FilterChip>
              {q.data.sections.map((s) => (
                <FilterChip key={s.source} active={source === s.source} onClick={() => setSource(s.source)}>
                  {s.title} · {s.count}
                </FilterChip>
              ))}
            </fieldset>
          ) : null}
          <ul className="divide-y divide-line">
            {shown.map((a) => (
              <li key={a.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
                <span
                  aria-hidden
                  className={clsx(
                    'mt-1.5 hidden size-2.5 shrink-0 rounded-[2px] sm:block',
                    a.severity === 'critical'
                      ? 'bg-danger'
                      : a.severity === 'warning'
                        ? 'bg-warning'
                        : 'bg-ink-3',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      tone={a.severity === 'critical' ? 'danger' : a.kind === 'action' ? 'accent' : 'neutral'}
                    >
                      {a.sourceTitle}
                    </Badge>
                    {a.kind === 'action' ? <span className="text-[12px] text-ink-2">ждёт вас</span> : null}
                    {a.due ? (
                      <span className={clsx('text-[12px]', a.overdue ? 'text-danger' : 'text-ink-2')}>
                        {a.overdue ? 'просрочено, ' : ''}срок {date(a.due)}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-[14px] leading-5 font-medium break-words text-ink">{a.title}</p>
                  {a.details || a.projectCode ? (
                    <p className="mt-0.5 line-clamp-2 text-[13px] break-words text-ink-2">
                      {[a.projectCode, a.details].filter(Boolean).join(' · ')}
                    </p>
                  ) : null}
                </div>
                <Link
                  to={a.actionPath}
                  className="inline-flex h-11 shrink-0 items-center justify-center rounded-md border border-line-strong px-3 text-[13px] font-medium text-ink hover:bg-surface-2"
                >
                  {a.actionLabel}
                </Link>
              </li>
            ))}
          </ul>
          {items.length > LIMIT ? (
            <button
              type="button"
              onClick={() => setAll(!all)}
              className="h-11 self-start rounded-md px-3 text-[13px] font-medium text-accent-text underline-offset-4 hover:underline"
            >
              {all ? 'Свернуть' : `Показать все: ${items.length}`}
            </button>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx(
        'inline-flex h-11 items-center rounded-md border px-3 text-[13px] font-medium',
        active ? 'border-ink bg-ink text-on-ink' : 'border-line-strong text-ink hover:bg-surface-2',
      )}
    >
      {children}
    </button>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'danger' | 'warning';
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-line px-3 py-2">
      <span
        className={clsx(
          'num text-[18px] leading-6 font-semibold',
          tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-ink',
        )}
      >
        {typeof value === 'number' ? num(value) : value}
      </span>
      <span className="text-[12px] text-ink-2">{label}</span>
    </div>
  );
}

export function PsdDigestTiles({ d, projectId }: { d: PsdDigest; projectId: string }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="комплектов" value={d.sets} />
        <Tile label="в производстве" value={d.inProduction} />
        <Tile label="ждём от проектировщика" value={d.expected} />
        <Tile label="просрочено" value={d.overdue} tone={d.overdue ? 'danger' : undefined} />
        <Tile label="в карантине" value={d.quarantine} tone={d.quarantine ? 'danger' : undefined} />
        <Tile
          label="открытых вопросов"
          value={d.openQuestions}
          tone={d.openQuestions ? 'warning' : undefined}
        />
        <Tile label="открытых замечаний" value={d.openRemarks} tone={d.openRemarks ? 'warning' : undefined} />
      </div>
      <Link
        to="/psd"
        search={{ project: projectId }}
        className="inline-flex h-11 items-center self-start text-[14px] font-medium text-accent-text underline-offset-4 hover:underline"
      >
        Комплекты ПСД →
      </Link>
    </div>
  );
}

export function ResourcesDigestTiles({ d, projectId }: { d: ResourcesDigest; projectId: string }) {
  return (
    <div className="flex flex-col gap-3">
      {d.week ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile label="людей нужно в неделю" value={d.needPeople} />
          <Tile label="машин нужно в неделю" value={d.needMachines} />
          <Tile
            label="профессий не хватает"
            value={d.shortages.length}
            tone={d.shortages.length ? 'warning' : undefined}
          />
          <Tile
            label="ресурсов без ставки"
            value={d.missingRates}
            tone={d.missingRates ? 'warning' : undefined}
          />
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">
          На этой неделе работ с ресурсами по графику нет.
          {d.missingRates ? ` Ресурсов без ставки: ${d.missingRates}.` : ''}
        </p>
      )}
      {d.shortages.length ? (
        <ul className="flex flex-col gap-1 text-[13px] text-ink-2">
          {d.shortages.map((s) => (
            <li key={s.name}>
              {s.name}: нужно {num(s.need)}, на вахте {num(s.available)}
            </li>
          ))}
        </ul>
      ) : null}
      <Link
        to="/resources"
        search={{ project: projectId }}
        className="inline-flex h-11 items-center self-start text-[14px] font-medium text-accent-text underline-offset-4 hover:underline"
      >
        Люди, техника и вахты →
      </Link>
    </div>
  );
}

/** Сводки ПСД и ресурсов проекта: показываются тем, у кого есть право на раздел */
export function ProjectDigests({ projectId, only }: { projectId: string; only?: 'psd' | 'resources' }) {
  const me = useMe();
  // Сводки — внутренние: у внешних участников запроса нет
  const q = useQuery({ ...projectAttentionQuery(projectId), enabled: Boolean(me.tenant?.staff) });
  if (!q.data) return null;
  const { psd, resources } = q.data;
  return (
    <>
      {psd && only !== 'resources' ? (
        <Panel title="ПСД: сводка" meta={psd.sets ? `${psd.sets} компл.` : undefined}>
          <PsdDigestTiles d={psd} projectId={projectId} />
        </Panel>
      ) : null}
      {resources && only !== 'psd' ? (
        <Panel title="Ресурсы: эта неделя" meta={resources.week ? `с ${date(resources.week)}` : undefined}>
          <ResourcesDigestTiles d={resources} projectId={projectId} />
        </Panel>
      ) : null}
    </>
  );
}
